import { timelineHeight } from "@/app/activity/components/timeline/activityTimeline/constants"
import {
  useGetTimePerWidth,
  useUpdateDraggingSession,
} from "@/app/activity/components/timeline/activityTimeline/hooks"
import TimelineItemBox from "@/app/activity/components/timeline/activityTimeline/timelineItemBox/TimelineItemBox"
import TimelineTick from "@/app/activity/components/timeline/activityTimeline/timelineTick/TimelineTick"

import {
  Magnification,
  RangeKey,
  TimelineItemBoxProps,
} from "@/app/activity/components/timeline/activityTimeline/type"
import { formatSessionBoxItems } from "@/app/activity/components/timeline/activityTimeline/utils"
import {
  createActivityCommonStore,
  createActivityStore,
  createSessionStore,
} from "@/app/activity/store"

import { colors } from "@/components/colors"

import { pointer } from "d3-selection"
import { add, sub } from "date-fns"

import { MouseEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"

import "./sessionTimeline.scss"
import moment from "moment"
import { getTimezoneOffset } from "@/utils/format"
import { clamp, cloneDeep, debounce } from "lodash"
import { Session } from "@/types/session"
import TimelineAddBox from "@/app/activity/components/timeline/sessionTimeline/timelineAddbox/TimelineAddBox.tsx"

import GuidLine from "@/app/activity/components/guideline/activityGuideLine/ActivityGuideline"
import { useTranslation } from "react-i18next"
import classNames from "classnames"
import SessionGuideLine from "@/app/activity/components/guideline/sessionGuideline/SessionGuideline"
import {
  SessionTimelineBoxProps,
  SessionTimelineProps,
} from "@/app/activity/components/timeline/sessionTimeline/type"
import { useUpsertActivityMutation } from "@/hooks/api/useActivityQuery"
import { createGlobalStore } from "@/stores/global"
import { Activity } from "@/types/activity"
import { GuideLine } from "@/app/activity/store/type"
import { getGuideLineDate } from "@/app/activity/components/utils/util.ts"

const NO_SESSION_TEXT_FONT_SIZE = 12

export default function SessionTimeline({
  rangeKey = RangeKey.ACTIVITY,

  currentItemList,
  onBoxClick,
}: SessionTimelineProps) {
  const ref = useRef<SVGSVGElement>(null)
  const svgContRef = useRef<HTMLDivElement>(null)
  const interactiveRectRef = useRef<SVGRectElement>(null)
  const sessionRef = useRef<Session | undefined>(undefined)
  const lastUpdateRef = useRef<number>(0)
  const { t, i18n } = useTranslation()

  const activityRef = useRef<Activity | undefined>(undefined)

  const [originalWidth, setOriginalWidth] = useState(1)
  const [width, setWidth] = useState(1)

  const [boxFillColor, setBoxFillColor] = useState("")
  const [boxStrokeColor, setBoxStrokeColor] = useState("")
  const [translateX, setTranslateX] = useState(0)

  const [mouseGuidelineX, setMouseGuidelineX] = useState(0)
  const [mouseGuidelineDate, setMouseGuidelineDate] = useState<Date>()
  const [isMouseOver, setIsMouseOver] = useState(false)

  const [isRightGuidelineVisible, setIsRightGuidelineVisible] = useState(false)
  const [rightGuidelineX, setRightGuidelineX] = useState(0)
  const [rightGuidelineXWithRemain, setRightGuidelineXWithRemain] = useState(0)
  const [rightGuidelineDate, setRightGuidelineDate] = useState<Date>()

  const [isLeftGuidelineVisible, setIsLeftGuidelineVisible] = useState(false)
  const [leftGuidelineX, setLeftGuidelineX] = useState(0)
  const [leftGuidelineXWithRemain, setLeftGuidelineXWithRemain] = useState(0)
  const [leftGuidelineDate, setLeftGuidelineDate] = useState<Date>()

  const [prevMagnification, setPrevMagnification] = useState<number>(Magnification.INIT)

  const dateIdx = [0]

  const {
    selectedSession,
    setSessionList,
    sessionList,
    setSelectedSession,
    setConfirmSaveDialogOpen,
    temporalSession,
    setTemporalSessionData,
    sessionGuideLine,
    setSessionGuideLine,
    upsertDialogOpen,
  } = createSessionStore((state) => ({
    selectedSession: state.session,
    setSelectedSession: state.setSession,
    setSessionList: state.setSessions,
    sessionList: state.sessions,
    setConfirmSaveDialogOpen: state.setConfirmSaveDialogOpen,
    temporalSession: state.temporarySession,
    setTemporalSessionData: state.setTemporarySession,
    sessionGuideLine: state.sessionGuideLine,
    setSessionGuideLine: state.setSessionGuideLine,
    upsertDialogOpen: state.upsertDialogOpen,
  }))

  const {
    setSelectedDate,
    selectedDate,
    setTimePerWidth,
    setTimelineWidth,
    magnification,
    timelineWidth,
    range: { dateStart: dateStart, dateFinish: dateFinish },
    remainRightArea = 0,
    setRemainRightArea,
    setSessionMouseGuideLine,
  } = createActivityCommonStore((state) => ({
    selectedDate: state.selectedDate,
    setSelectedDate: state.setSelectedDate,
    setTimePerWidth: state.setTimePerWidth,
    setTimelineWidth: state.setTimelineWidth,
    timelineWidth: state.timelineWidth,
    range: state.range,
    magnification: state.magnification,
    remainRightArea: state.remainingArea,
    setRemainRightArea: state.setRemainingArea,
    setSessionMouseGuideLine: state.setSessionMouseGuideLine,
  }))

  const magnificationRef = useRef(magnification || Magnification.INIT)
  magnificationRef.current = magnification || Magnification.INIT

  // onKeyDown 은 mount 시 한 번만 등록되어 closure 가 stale 하므로(deps []),
  // 원본 선택 세션을 ref 로 항상 최신화해 ESC 핸들러에서 변경 여부를 정확히 비교한다 (WEB-2170).
  const selectedSessionRef = useRef<Session | undefined>(undefined)
  selectedSessionRef.current = selectedSession

  const { activity, setActivity, activityGuideLine, originalActivity } = createActivityStore(
    (state) => ({
      activity: state.tempActivity,
      setActivity: state.setTempActivity,
      activityGuideLine: state.activityGuideLine,
      originalActivity: state.activity,
    }),
  )

  const { setSnackBar } = createGlobalStore((state) => ({
    setSnackBar: state.setSnackBar,
  }))

  const { mutateAsync: updateActivityMutation } = useUpsertActivityMutation(activity?.idfActivity)
  const timePerWidth = useGetTimePerWidth({ dateStart, dateFinish, chartWidth: width })

  const timezoneOffset = getTimezoneOffset()
  const updateDraggingSession = useUpdateDraggingSession(width)

  const sessionListProps = useMemo(() => {
    return (
      formatSessionBoxItems(
        (currentItemList as Session[])?.sort((a, b) => a.idfSession - b.idfSession) || [],
        width,
        dateStart,
        dateFinish,
      ) as TimelineItemBoxProps[]
    ).map((item) => ({ ...item, className: "session-box" }))
  }, [currentItemList, width, dateStart, dateFinish])

  const noSessionText = useMemo(() => {
    return t("messages.addOneOrMoreSessions")
  }, [i18n.language, t])

  const boxProps = useMemo(() => {
    return sessionListProps
  }, [sessionListProps])

  const boxSvgHeight = useMemo(() => {
    const yIndexArr = sessionListProps.map((box: Record<string, unknown>) => box.yIndex)
    const max = Math.max(...(yIndexArr as number[])) + 2

    return Math.max(max * 5 + max * 35, 188)
  }, [sessionListProps])

  const tickStartDate = useMemo(() => {
    if (remainRightArea && dateStart) {
      return new Date(dateStart.valueOf() - (remainRightArea / 2) * timePerWidth)
    }

    if (dateStart) {
      return new Date(dateStart.valueOf())
    }

    return dateStart
  }, [remainRightArea, dateStart])

  const tickFinishDate = useMemo(() => {
    if (remainRightArea && dateFinish) {
      return new Date(dateFinish.valueOf() + (remainRightArea / 2) * timePerWidth)
    }

    if (dateFinish) {
      return new Date(dateFinish.valueOf())
    }

    return dateFinish
  }, [remainRightArea, dateFinish])

  const resizeHandler = () => {
    if (svgContRef.current) {
      const containerWidth = svgContRef.current.clientWidth
      setOriginalWidth(containerWidth)
      setWidth(containerWidth * magnificationRef.current)
    }
  }

  const getBoxFillColor = () => {
    if (selectedSession && selectedSession.idfSession) {
      return colors.neutral.grey[300]
    }

    return boxFillColor
  }

  const getBoxStrokeColor = () => {
    if (selectedSession && selectedSession.idfSession) {
      return colors.neutral.grey[300]
    }

    return boxStrokeColor
  }

  const timelineDragging = (e: MouseEvent<SVGRectElement>) => {
    const targetIsChildren = ref.current?.contains(e.target as Node)

    if (!targetIsChildren) {
      return
    }

    if (selectedSession) {
      return
    }

    if (isLeftGuidelineVisible && isRightGuidelineVisible) {
      return
    }

    if (!isLeftGuidelineVisible) {
      setIsLeftGuidelineVisible(true)
      setLeftGuidelineX(mouseGuidelineX - (remainRightArea || 0) / 2)
      setLeftGuidelineDate(mouseGuidelineDate)

      return
    }

    if (isLeftGuidelineVisible && !isRightGuidelineVisible) {
      const endX = mouseGuidelineX - (remainRightArea || 0) / 2

      if (endX > leftGuidelineX) {
        setIsRightGuidelineVisible(true)
        setRightGuidelineX(endX)
        setRightGuidelineDate(mouseGuidelineDate)
      }

      return
    }
  }

  const getMouseX = (event: React.MouseEvent) => {
    const rect = svgContRef.current?.getBoundingClientRect()
    if (!rect) return 0
    return event.clientX - rect.left + (svgContRef.current?.scrollLeft || 0)
  }

  const onMouseEnter = (event: React.MouseEvent) => {
    const { startX, endX } = activityGuideLine as GuideLine
    const x = getMouseX(event)
    const point = clamp(x, startX + remainRightArea / 2, (endX as number) + remainRightArea / 2)
    setMouseGuidelineX(point)
    setIsMouseOver(true)

    if (dateStart && dateFinish) {
      getGuideLineDate({ position: point, tickStartDate, tickFinishDate, width, originalWidth })
    }
  }

  const onMouseLeave = () => {
    setIsMouseOver(false)
  }

  const onMouseMove = (event: React.MouseEvent) => {
    const { startX, endX } = activityGuideLine as GuideLine
    const x = getMouseX(event)
    const point = clamp(x, startX + remainRightArea / 2, (endX as number) + remainRightArea / 2)
    setMouseGuidelineX(point)

    if (dateStart && dateFinish) {
      setMouseGuidelineDate(
        getGuideLineDate({ position: point, tickStartDate, tickFinishDate, width, originalWidth }),
      )
    }
  }

  const onKeyDown = function (e?: KeyboardEvent) {
    if (e?.code !== "Escape") {
      return
    }

    if (sessionRef.current?.idfSession) {
      // 세션 시간이 실제로 변경된 경우에만 확인 다이얼로그를 띄운다.
      // sessionRef.current = 현재 작업본, selectedSessionRef.current = 원본
      // (ActivityDetailMode onBoxClick 의 temporarySession vs selectedSession 비교와 동일 기준).
      // 변경 없이 ESC 만 누른 경우엔 다이얼로그 없이 아래 선택 해제 로직으로 진행한다 (WEB-2170).
      const original = selectedSessionRef.current
      const isSessionTimeChanged =
        !!original &&
        (sessionRef.current.dateStart !== original.dateStart ||
          sessionRef.current.dateFinish !== original.dateFinish)

      if (isSessionTimeChanged) {
        setConfirmSaveDialogOpen(true)

        return
      }
    }
    if (!isLeftGuidelineVisible || !isRightGuidelineVisible) {
      setSelectedSession(undefined)
    }
    cancelSelection()
  }

  const onDrag = debounce((event) => {
    updateDraggingSession({
      event,
      ref,
      sessionRef,
      setTemporalSessionData,
    })
  }, 10)

  const cancelSelection = () => {
    setIsRightGuidelineVisible(false)
    setRightGuidelineX(0)
    setRightGuidelineDate(undefined)

    setIsLeftGuidelineVisible(false)
    setLeftGuidelineX(0)
    setLeftGuidelineDate(undefined)
  }

  const handleScrollEvent = (event: React.UIEvent<HTMLDivElement>) => {
    event.stopPropagation()
    event.preventDefault()
    const target = event.target as HTMLElement
    const scrollEle = document.querySelector(".simplebar-content-wrapper")

    if (scrollEle) {
      scrollEle.scrollLeft = target.scrollLeft
    }
  }

  const MAX_DURATION_MS = 8 * 60 * 60 * 1000

  const onDragUpdateActivity = ({
    direction,
    moveX,
    startX,
    endX,
  }: {
    direction: string
    moveX: number
    isActivity: boolean
    startX?: number
    endX?: number
  }) => {
    let newPosition: number
    let propertyKey: string

    if (direction === "right") {
      newPosition = (endX as number) + moveX
      newPosition = clamp(newPosition, startX as number, timelineWidth)
      propertyKey = "dateFinish"
    } else {
      newPosition = (startX as number) + moveX
      newPosition = clamp(newPosition, 0, endX as number)
      propertyKey = "dateStart"
    }

    if (temporalSession) {
      const updatedSession = { ...temporalSession } as Record<string, unknown>

      updatedSession[propertyKey] = new Date(
        (dateStart as Date).valueOf() + newPosition * timePerWidth,
      ).toISOString()

      setTemporalSessionData(updatedSession as Session)
      sessionRef.current = updatedSession as Session

      return
    }

    if (!activity) {
      return
    }

    const newDate = new Date((dateStart as Date).valueOf() + newPosition * timePerWidth)
    let activityStart = new Date(activity.dateStart)
    let activityFinish = new Date(activity.dateFinish)

    if (propertyKey === "dateStart") {
      activityStart = newDate
    } else {
      activityFinish = newDate
    }

    const duration = activityFinish.getTime() - activityStart.getTime()
    if (duration > MAX_DURATION_MS) {
      return
    }

    const updatedActivity = { ...activity } as Record<string, unknown>
    updatedActivity[propertyKey] = newDate.toISOString()

    setActivity(updatedActivity as Activity)
    activityRef.current = updatedActivity as Activity
  }

  const updateActivity = async (activityToUpdate: Activity) => {
    try {
      if (activityToUpdate) {
        await updateActivityMutation({
          idfActivity: activityToUpdate.idfActivity as number,
          payload: activityToUpdate,
        })

        setSnackBar({
          message: t("messages.successUpdate"),
          severity: "success",
          open: true,
        })
      }
    } catch (error) {
      console.error(error)

      if (originalActivity) {
        setActivity(originalActivity)
      }
    }
  }

  useEffect(() => {
    const result = Math.abs(Math.max(0, originalWidth - width))

    setRemainRightArea?.(result)
  }, [magnification, width, originalWidth])

  useLayoutEffect(() => {
    if (isRightGuidelineVisible) {
      setSessionGuideLine({
        ...(sessionGuideLine as GuideLine),
        startX: leftGuidelineX,
        endX: rightGuidelineX,
      })
    } else if (isLeftGuidelineVisible) {
      setSessionGuideLine({
        startX: leftGuidelineX + (remainRightArea || 0) / 2,

        startDate: leftGuidelineDate as Date,
      })
    } else {
      setSessionGuideLine(undefined)
    }
  }, [isRightGuidelineVisible, leftGuidelineX, rightGuidelineX, leftGuidelineDate])

  useEffect(() => {
    setSessionMouseGuideLine({
      x: mouseGuidelineX,
      date: mouseGuidelineDate as Date,
      isVisible: isMouseOver,
    })
  }, [mouseGuidelineDate, mouseGuidelineX, isMouseOver])

  useEffect(() => {
    const xPosition = width
    const criteria = xPosition / width

    if (criteria >= 2) {
      setSelectedDate(sub(selectedDate, { days: 1 }))
      setTranslateX(0)
    }

    if (criteria <= 0) {
      setSelectedDate(add(selectedDate, { days: 1 }))
      setTranslateX(0)
    }
  }, [translateX])

  useEffect(() => {
    setBoxStrokeColor(colors.primary[500])

    setBoxFillColor(colors.primary[200])
  }, [])

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown)

    return () => {
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [])

  useEffect(() => {
    if (!isLeftGuidelineVisible) {
      return
    }

    const handleOutsideClick = (e: MouseEvent) => {
      if (svgContRef.current && !svgContRef.current.contains(e.target as Node)) {
        cancelSelection()
      }
    }

    document.addEventListener("mousedown", handleOutsideClick)

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick)
    }
  }, [isLeftGuidelineVisible])

  useEffect(() => {
    if (svgContRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const containerWidth = entry.contentRect.width
          setOriginalWidth(containerWidth)
          setWidth(containerWidth * magnificationRef.current)
        })
      })

      resizeObserver.observe(svgContRef.current)

      setOriginalWidth(svgContRef.current.clientWidth)

      return () => {
        resizeObserver.disconnect()
      }
    }
  }, [])

  useEffect(() => {
    window.addEventListener("resize", resizeHandler)

    return () => {
      window.removeEventListener("resize", resizeHandler)
    }
  }, [])

  useEffect(() => {
    setWidth(() => originalWidth * (magnification || Magnification.INIT))
    setPrevMagnification(magnification || Magnification.INIT)

    if ((magnification as number) < prevMagnification) {
      setLeftGuidelineX(leftGuidelineX / 2)
      setRightGuidelineX(rightGuidelineX / 2)
      if (temporalSession) {
        setTemporalSessionData({
          ...(temporalSession as Session),
          width: (temporalSession as unknown as Record<string, number>).width / 2,
          startX: (temporalSession as unknown as Record<string, number>).startX / 2,
        } as unknown as Session)
      }
    } else if ((magnification as number) > prevMagnification) {
      setLeftGuidelineX(leftGuidelineX * 2)
      setRightGuidelineX(rightGuidelineX * 2)
      if (temporalSession) {
        setTemporalSessionData({
          ...(temporalSession as Session),
          width: (temporalSession as unknown as Record<string, number>).width * 2,
          startX: (temporalSession as unknown as Record<string, number>).startX * 2,
        } as unknown as Session)
      }
    }
  }, [magnification, remainRightArea])

  useEffect(() => {
    setLeftGuidelineXWithRemain(() => leftGuidelineX + (remainRightArea || 0) / 2)
  }, [leftGuidelineX, remainRightArea])
  useEffect(() => {
    setRightGuidelineXWithRemain(() => rightGuidelineX + (remainRightArea || 0) / 2)
  }, [rightGuidelineX, remainRightArea])

  useEffect(() => {
    if (!upsertDialogOpen) {
      cancelSelection()
      setSelectedSession(undefined)
    }
  }, [upsertDialogOpen])

  useEffect(() => {
    if (selectedSession && (selectedSession as SessionTimelineBoxProps)?.startX === undefined) {
      const target = boxProps?.find((box) => box.idfSession === selectedSession?.idfSession)
      if (target) {
        const copied = cloneDeep(target) as unknown as Session

        setTemporalSessionData(copied)
        sessionRef.current = copied

        if (svgContRef.current && target.startY !== undefined) {
          svgContRef.current.scrollTop = Math.max(0, target.startY - 20)
        }
      }
    } else if (selectedSession) {
      const copied = cloneDeep(selectedSession) as unknown as Session

      setTemporalSessionData(copied)
      sessionRef.current = copied

      const target = boxProps?.find((box) => box.idfSession === selectedSession?.idfSession)
      if (svgContRef.current && target?.startY !== undefined) {
        svgContRef.current.scrollTop = Math.max(0, target.startY - 20)
      }
    } else {
      setTemporalSessionData(undefined)
      sessionRef.current = undefined
    }
  }, [selectedSession])

  useEffect(() => {
    if (temporalSession) {
      const target = sessionList?.find((box) => box.idfSession === temporalSession?.idfSession)

      if (target) {
        setSessionList(
          sessionList.map((session) => {
            if (session.idfSession === target.idfSession) {
              return temporalSession
            }

            return session
          }),
        )
      }
    }
  }, [temporalSession?.idfSession])

  useEffect(() => {
    if (temporalSession) {
      const target = boxProps?.find((box) => box.idfSession === temporalSession?.idfSession)

      if (target) {
        const updatedSession = {
          ...temporalSession,
          startX: target.startX,
          width: target.width,
          startY: target.startY,
        }

        setTemporalSessionData(updatedSession as Session)
        sessionRef.current = updatedSession as Session
      }
    }
  }, [dateStart, dateFinish, boxProps])

  useEffect(() => {
    setTimePerWidth(timePerWidth)
  }, [timePerWidth])

  useEffect(() => {
    if (width > 1) {
      setTimelineWidth(width)
    }
  }, [width])

  return (
    <div
      className={classNames("SessionTimelineContWrapper", {
        "SessionTimelineContWrapper--hoverable": !selectedSession && !isLeftGuidelineVisible,
      })}
      onScroll={handleScrollEvent}
      ref={svgContRef}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}>
      <svg
        className="session-timeline-tick"
        width={width > 1 ? Math.max(originalWidth, width) : "100%"}
        height="46px">
        <g>
          <rect height="100%" width="20" fill="url(#grad1)"></rect>
          <defs>
            <linearGradient id="grad1" x1="0%" x2="100%" y1="0%" y2="0%">
              <stop offset="0%" color={colors.neutral.black} stopOpacity={0.08} />
              <stop offset="100%" stopOpacity={0} />
            </linearGradient>
          </defs>
        </g>
        <g>
          <text x="-50" dx="100%" y="17" className="timezone-offset">
            {timezoneOffset}
          </text>
        </g>
        <g transform={`translate(${0},0)`}>
          {dateIdx.map((idx) => (
            <TimelineTick
              key={`tick-${idx}`}
              transform={`translate(${width * idx}, 0)`}
              width={Math.max(originalWidth, width)}
              rangeKey={rangeKey}
              dateStart={tickStartDate}
              dateFinish={tickFinishDate}
              magnification={magnification}
            />
          ))}
        </g>
        {isMouseOver && !isLeftGuidelineVisible && !selectedSession && (
          <SessionGuideLine
            isDateVisible={true}
            position={mouseGuidelineX}
            date={mouseGuidelineDate as Date}
          />
        )}
        {isLeftGuidelineVisible && (
          <SessionGuideLine
            isDateVisible={true}
            position={sessionGuideLine?.startX || leftGuidelineXWithRemain}
            date={sessionGuideLine?.startDate || (leftGuidelineDate as Date)}
          />
        )}
        {isLeftGuidelineVisible && !isRightGuidelineVisible && (
          <SessionGuideLine
            isDateVisible={true}
            position={mouseGuidelineX}
            date={mouseGuidelineDate as Date}
          />
        )}
        {isRightGuidelineVisible && (
          <SessionGuideLine
            isDateVisible={true}
            position={sessionGuideLine?.endX || rightGuidelineXWithRemain}
            date={sessionGuideLine?.endDate || (rightGuidelineDate as Date)}
          />
        )}

        <GuidLine
          isVisible
          isTimeVisible
          isSession
          isHandleVisible={!selectedSession}
          onDrag={onDragUpdateActivity}
          onDragEnd={() => {
            updateActivity(activityRef.current as Activity)
          }}
        />
      </svg>
      <svg
        className={classNames("SessionTimelineCont")}
        width={width > 1 ? Math.max(originalWidth, width) : "100%"}
        height={`${boxSvgHeight}px`}
        ref={ref}>
        <rect
          ref={interactiveRectRef}
          width="100%"
          height="100%"
          opacity="0"
          onClick={timelineDragging}
        />

        <g transform={`translate(${0},0)`}>
          {boxProps?.map((boxProp, index) => {
            if (
              (boxProp as Session)?.idfSession &&
              (boxProp as Session)?.idfSession === selectedSession?.idfSession
            ) {
              return
            }

            return (
              <TimelineItemBox
                key={(boxProp as Session)?.idfSession || index}
                {...boxProp}
                startX={boxProp.startX}
                fillColor={getBoxFillColor()}
                strokeColor={getBoxStrokeColor()}
                hoverable
                editable={false}
                isSession
                rangeKey={rangeKey}
                remainRightArea={remainRightArea / 2}
                onClick={(item) => {
                  cancelSelection()
                  onBoxClick?.(item)
                }}
              />
            )
          })}

          {temporalSession && (
            <TimelineItemBox
              {...(temporalSession as TimelineItemBoxProps)}
              className="session-box"
              fillColor={colors.primary[200]}
              strokeColor={colors.primary[500]}
              hoverable
              editable
              rangeKey={rangeKey}
              remainRightArea={remainRightArea / 2}
              onClick={(item) => {
                cancelSelection()
                onBoxClick?.(item)
              }}
              onDragStart={() => {
                updateDraggingSession({
                  event: { direction: "", moveX: 0 },
                  ref,
                  sessionRef,
                  setTemporalSessionData,
                  isDragStart: true,
                })
              }}
              onDrag={({ direction, moveX, event }) => {
                requestAnimationFrame(() => {
                  updateDraggingSession({
                    event: { direction, moveX, event },
                    ref,
                    sessionRef,
                    setTemporalSessionData,
                  })
                })
              }}
              onDragEnd={({ direction, moveX, event }) => {
                updateDraggingSession({
                  event: { direction, moveX, event },
                  ref,
                  sessionRef,
                  setTemporalSessionData,
                  isDragEnd: true,
                })
              }}
            />
          )}
          {currentItemList?.length === 0 && (
            <text
              alignmentBaseline="middle"
              textAnchor="middle"
              x={width / 2}
              y={timelineHeight / 2}
              className="no-session-text"
              fontWeight={400}
              fontSize={NO_SESSION_TEXT_FONT_SIZE}
              fill={colors.neutral.grey[500]}>
              {noSessionText}
            </text>
          )}
        </g>

        {isMouseOver && !isLeftGuidelineVisible && !selectedSession && (
          <SessionGuideLine
            isDateVisible={false}
            position={mouseGuidelineX}
            date={mouseGuidelineDate as Date}
          />
        )}
        {isLeftGuidelineVisible && (
          <SessionGuideLine
            isDateVisible={false}
            position={sessionGuideLine?.startX || leftGuidelineXWithRemain}
            date={sessionGuideLine?.startDate || (leftGuidelineDate as Date)}
          />
        )}
        {isLeftGuidelineVisible && !isRightGuidelineVisible && (
          <SessionGuideLine
            isDateVisible={false}
            position={mouseGuidelineX}
            date={mouseGuidelineDate as Date}
          />
        )}
        {isRightGuidelineVisible && (
          <SessionGuideLine
            isDateVisible={false}
            position={sessionGuideLine?.endX || rightGuidelineXWithRemain}
            date={sessionGuideLine?.endDate || (rightGuidelineDate as Date)}
          />
        )}

        <GuidLine
          isVisible
          isTimeVisible={false}
          isSession
          isHandleVisible={!selectedSession}
          onDrag={onDragUpdateActivity}
          onDragEnd={() => {
            updateActivity(activityRef.current as Activity)
          }}
        />

        {isLeftGuidelineVisible && (
          <TimelineAddBox
            x={leftGuidelineX + remainRightArea / 2}
            width={Math.max(
              0,
              (!isRightGuidelineVisible ? mouseGuidelineX - remainRightArea / 2 : rightGuidelineX) -
                leftGuidelineX,
            )}
            onClick={timelineDragging}
            dateStart={leftGuidelineDate as Date}
            dateFinish={rightGuidelineDate as Date}
            disabled={!isRightGuidelineVisible}
          />
        )}
      </svg>
    </div>
  )
}
